import { defaultFilter } from 'cmdk';
import type { CustomerResult } from '@/services/auth/types';
import type { Customer } from '@customerio/hydra-ui-api';
import { isEmptyString } from '@/utils/string';

/**
 * Maps a Customer to a CustomerResult for use in the command dialog.
 *
 * Title resolution order: name → email → "{Person} with id {id}" → "cio_id: {cio_id}"
 *
 * The `_created_in_customerio_at` attribute is a Unix timestamp (seconds) stored as a string.
 * It is converted to an ISO 8601 string for display; invalid or missing values are omitted.
 */
export function mapCustomerToSearchResult(
  customer: Customer,
  workspaceId: number,
  personTitleSingular = 'Person',
): CustomerResult {
  const cioCreatedAt = customer.attributes?.['_created_in_customerio_at'];
  const parsedCreatedAt =
    cioCreatedAt != null && !isEmptyString(cioCreatedAt) ? Number.parseInt(cioCreatedAt, 10) : Number.NaN;
  const formattedCreatedAt = !Number.isNaN(parsedCreatedAt)
    ? new Date(parsedCreatedAt * 1000).toISOString()
    : undefined;

  const name = customer.attributes['name'];
  const email = customer.attributes['email'];
  const nameString = typeof name === 'string' && !isEmptyString(name) ? name : undefined;
  const emailString = typeof email === 'string' && !isEmptyString(email) ? email : undefined;
  // Prefer the external id, else cio_id — matches people navigation elsewhere (activity-logs).
  const identifier = !isEmptyString(customer.id) ? customer.id : getCustomerKey(customer);
  const title =
    nameString ??
    emailString ??
    (!isEmptyString(customer.id) ? `${personTitleSingular} with id ${customer.id}` : `cio_id: ${identifier}`);

  return {
    kind: 'customer',
    doc_id: identifier,
    title,
    url: `${window.location.origin}/workspaces/${workspaceId}/people/${identifier}`,
    other: {
      id: customer.id,
      email: emailString,
      unsubscribed: customer.unsubscribed,
      created_at: formattedCreatedAt,
    },
  };
}

/**
 * Builds the query string for the people (customers) endpoint.
 * The endpoint supports wildcard searches — if the input already contains '@' (email/domain)
 * or '*' (explicit wildcard), pass it through unchanged.
 * Otherwise append '*' to treat plain text as a prefix match (e.g. "john" → "john*").
 *
 * An empty string intentionally returns '*' (match all customers), which is the correct
 * API behavior for a "show all people" mode. The query is currently gated by
 * isSearchingEnabled (requires non-empty input), so this path is not yet reachable —
 * but '*' is the right value if that gate is ever relaxed.
 */
export function buildPeopleSearchQuery(query: string): string {
  if (query.includes('@') || query.includes('*')) return query;
  return `${query}*`;
}

/**
 * Base64-encoded `filters` value for an exact `id` lookup. `id` is case-sensitive (pass it
 * verbatim)
 */
export function buildPeopleIdFilter(query: string): string {
  const filter = { and: [{ attribute: { field: 'id', operator: 'eq', value: query } }] };
  return btoa(encodeURIComponent(JSON.stringify(filter)));
}

/**
 * Used for de-duplicating results across exact-`id` and email searches.
 */
export function getCustomerKey(customer: Pick<Customer, 'identifiers'>): string {
  return customer.identifiers['cio_id'];
}

/**
 * Combines the results of email prefix search and exact-id lookup
 */
export function mergePeopleResults(emailMatches: Customer[], idMatch: Customer | undefined): Customer[] {
  if (idMatch == null) return emailMatches;
  const idKey = getCustomerKey(idMatch);
  return [idMatch, ...emailMatches.filter((customer) => getCustomerKey(customer) !== idKey)];
}

/**
 * Scores a customer result against the current query.
 *
 * Scores against both title (name → fallback) and email separately, then takes the max.
 * This handles email-format queries where the customer has a name set — the title would
 * score near-zero against an email query, but the email attribute scores correctly.
 */
export function scoreCustomerResult(result: CustomerResult, query: string): number {
  // Fallback exact-id match by string compare. Case-sensitive.
  if (!isEmptyString(result.other.id) && result.other.id === query) {
    return 1;
  }
  const titleScore = defaultFilter(result.title, query);
  const emailScore = result.other.email != null ? defaultFilter(result.other.email, query) : 0;
  return Math.max(titleScore, emailScore);
}
